import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { AuthorizedButton } from '@/components/ui/AuthorizedButton'
import { usePermissions } from '@/hooks/use-permissions'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Loader2, ChevronDown, ChevronUp, Plus, X, Crown, TestTube } from 'lucide-react'
import { usePools, useUpdatePool } from '@/hooks/use-pools'
import { useToast } from '@/hooks/use-toast'
import type { components } from '../../../../../types/flex-api'
import { FORECAST_PURPOSES, type ForecastPurpose } from '@/lib/entity-icons'
import { FALLBACK_BEHAVIOR_DESCRIPTIONS } from '@/lib/pool-control'

type Pool = components['schemas']['Pool']

type ForecastEntry = { purpose: ForecastPurpose; isActive: boolean }

function forecastConfigsToEntries(configs: Pool['forecastConfigs']): ForecastEntry[] {
  return configs?.map((c) => ({ purpose: c.purpose as ForecastPurpose, isActive: c.isActive })) ?? []
}

interface EditPoolDialogProps {
  open: boolean
  onClose: () => void
  pool: Pool
  onFallbackSetToTou?: () => void
}

function poolTagsToList(tags: Pool['tags']): { key: string; value: string }[] {
  if (!tags) return []
  return Object.entries(tags as Record<string, string>).map(([key, value]) => ({ key, value }))
}

export function EditPoolDialog({ open, onClose, pool, onFallbackSetToTou }: EditPoolDialogProps) {
  const { can } = usePermissions()
  const { data: pools = [] } = usePools()
  const { mutateAsync: updatePool } = useUpdatePool()
  const { toast } = useToast()

  const [name, setName] = useState(pool.name)
  const [isDefault, setIsDefault] = useState(!!(pool as any).isDefault)
  const [tags, setTags] = useState<{ key: string; value: string }[]>(
    poolTagsToList(pool.tags)
  )
  const [showTags, setShowTags] = useState(tags.length > 0)
  const [fallbackBehavior, setFallbackBehavior] = useState<'none' | 'last_possible_moment' | 'time_of_use'>(
    pool.controlConfig?.fallbackBehavior ?? 'last_possible_moment'
  )
  const [dryRun, setDryRun] = useState(pool.controlConfig?.dryRun ?? false)
  const isEvPool = pool.controlConfig?.purpose === 'intraday' || pool.controlConfig?.purpose === 'intraday_cloud'
  const [evChargingEfficiencyFactor, setEvChargingEfficiencyFactor] = useState<string>(
    pool.evChargingEfficiencyFactor != null ? String(pool.evChargingEfficiencyFactor) : ''
  )
  const [loading, setLoading] = useState(false)
  const [forecastEntries, setForecastEntries] = useState<ForecastEntry[]>(
    forecastConfigsToEntries(pool.forecastConfigs)
  )

  // Re-sync state if the pool prop changes (e.g. after a save)
  useEffect(() => {
    setName(pool.name)
    setIsDefault(!!(pool as any).isDefault)
    const list = poolTagsToList(pool.tags)
    setTags(list)
    setShowTags(list.length > 0)
    setFallbackBehavior(pool.controlConfig?.fallbackBehavior ?? 'last_possible_moment')
    setDryRun(pool.controlConfig?.dryRun ?? false)
    setEvChargingEfficiencyFactor(
      pool.evChargingEfficiencyFactor != null ? String(pool.evChargingEfficiencyFactor) : ''
    )
    setForecastEntries(forecastConfigsToEntries(pool.forecastConfigs))
  }, [pool])

  const toggleForecastPurpose = (purpose: ForecastPurpose) => {
    setForecastEntries((prev) => {
      const exists = prev.find((e) => e.purpose === purpose)
      if (exists) {
        return prev.filter((e) => e.purpose !== purpose)
      }
      return [...prev, { purpose, isActive: true }]
    })
  }

  const toggleForecastActive = (purpose: ForecastPurpose) => {
    setForecastEntries((prev) =>
      prev.map((e) => e.purpose === purpose ? { ...e, isActive: !e.isActive } : e)
    )
  }

  const validateForm = (): string | null => {
    if (!name.trim()) {
      return 'Pool name is required'
    }

    const duplicate = pools.find(
      (p) => p.name.toLowerCase() === name.trim().toLowerCase() && p.poolID !== pool.poolID
    )
    if (duplicate) {
      return `A pool named "${name.trim()}" already exists. Please choose a different name.`
    }

    const tagKeys = tags.map((t) => t.key.trim()).filter((k) => k)
    if (tagKeys.length !== new Set(tagKeys).size) {
      return 'Tag keys must be unique'
    }

    for (const tag of tags) {
      if (tag.value.trim() && !tag.key.trim()) {
        return 'Tag keys cannot be empty'
      }
    }

    if (evChargingEfficiencyFactor !== '') {
      const v = parseFloat(evChargingEfficiencyFactor)
      if (isNaN(v) || v < 0.5 || v > 1) {
        return 'EV charging efficiency factor must be a number between 0.5 and 1'
      }
    }

    return null
  }

  const handleSubmit = async () => {
    const validationError = validateForm()
    if (validationError) {
      toast({ title: 'Validation Error', description: validationError, variant: 'destructive' })
      return
    }

    setLoading(true)
    try {
      const validTags = tags.filter((t) => t.key.trim())
      const currentKeys = new Set(validTags.map((t) => t.key.trim()))

      // Build merge-patch: send updated/added tags, and null out any removed keys
      const tagsObject: Record<string, string | null> = {}
      for (const tag of validTags) {
        tagsObject[tag.key.trim()] = tag.value.trim()
      }
      for (const key of Object.keys((pool.tags as Record<string, string>) ?? {})) {
        if (!currentKeys.has(key)) {
          tagsObject[key] = null
        }
      }

      await updatePool({
        poolID: pool.poolID,
        data: {
          name: name.trim(),
          isDefault,
          tags: tagsObject,
          forecastConfigs: forecastEntries.map(({ purpose, isActive }) => ({ purpose, isActive })),
          ...(isEvPool && {
            evChargingEfficiencyFactor:
              evChargingEfficiencyFactor !== '' ? parseFloat(evChargingEfficiencyFactor) : null,
          }),
          ...(pool.controlConfig && {
            controlConfig: {
              dryRun,
              ...(pool.controlConfig.purpose === 'intraday_cloud' && { fallbackBehavior }),
            },
          }),
        },
      })
      toast({ title: 'Saved', description: 'Pool updated successfully' })
      const wasNotTou = pool.controlConfig?.fallbackBehavior !== 'time_of_use'
      const isNowTou = fallbackBehavior === 'time_of_use'
      if (wasNotTou && isNowTou) {
        onFallbackSetToTou?.()
      }
      onClose()
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update pool',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    if (!loading) {
      setName(pool.name)
      setIsDefault(!!(pool as any).isDefault)
      const list = poolTagsToList(pool.tags)
      setTags(list)
      setShowTags(list.length > 0)
      setFallbackBehavior(pool.controlConfig?.fallbackBehavior ?? 'last_possible_moment')
      setDryRun(pool.controlConfig?.dryRun ?? false)
      setEvChargingEfficiencyFactor(
        pool.evChargingEfficiencyFactor != null ? String(pool.evChargingEfficiencyFactor) : ''
      )
      setForecastEntries(forecastConfigsToEntries(pool.forecastConfigs))
      onClose()
    }
  }

  const addTag = () => setTags([...tags, { key: '', value: '' }])

  const removeTag = (index: number) => setTags(tags.filter((_, i) => i !== index))

  const updateTag = (index: number, field: 'key' | 'value', value: string) => {
    const newTags = [...tags]
    newTags[index][field] = value
    setTags(newTags)
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>Edit Pool</CardTitle>
          <CardDescription>Update pool name, default status, or tags</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Pool Name */}
          <div className="space-y-2">
            <Label htmlFor="edit-pool-name">
              Pool Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="edit-pool-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
            />
          </div>

          {/* Default Pool */}
          <div className="flex items-start space-x-2">
            <Checkbox
              id="edit-isDefault"
              checked={isDefault}
              onCheckedChange={(checked: boolean | 'indeterminate') => setIsDefault(checked === true)}
              disabled={loading}
              className="mt-1"
            />
            <div className="flex-1">
              <Label htmlFor="edit-isDefault" className="cursor-pointer flex items-center gap-2">
                <Crown className="h-4 w-4" />
                Default Pool
              </Label>
              <p className="text-xs text-muted-foreground mt-1">
                New systems in this account will automatically be added to the default pool
              </p>
            </div>
          </div>

          {pool.controlConfig && (
            <div className="space-y-4 p-4 border rounded-lg bg-muted/50">
              <h3 className="font-medium text-sm">Control Configuration</h3>
              {pool.controlConfig.purpose === 'intraday_cloud' && (
                <div className="space-y-2">
                  <Label htmlFor="edit-fallbackBehavior">Fallback Behavior</Label>
                  <Select
                    value={fallbackBehavior}
                    onValueChange={(v) => setFallbackBehavior(v as typeof fallbackBehavior)}
                    disabled={loading}
                  >
                    <SelectTrigger id="edit-fallbackBehavior">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="last_possible_moment">Last possible moment</SelectItem>
                      <SelectItem value="time_of_use">Time of use</SelectItem>
                    </SelectContent>
                  </Select>
                  {FALLBACK_BEHAVIOR_DESCRIPTIONS[fallbackBehavior] && (
                    <p className="text-xs text-muted-foreground mt-2">
                      {FALLBACK_BEHAVIOR_DESCRIPTIONS[fallbackBehavior]}
                    </p>
                  )}
                </div>
              )}
              {isEvPool && (
                <div className="space-y-2">
                  <Label htmlFor="edit-evChargingEfficiencyFactor">EV Charging Efficiency Factor</Label>
                  <Input
                    id="edit-evChargingEfficiencyFactor"
                    type="number"
                    min={0.5}
                    max={1}
                    step={0.01}
                    placeholder="e.g. 0.95 (leave empty to unset)"
                    value={evChargingEfficiencyFactor}
                    onChange={(e) => setEvChargingEfficiencyFactor(e.target.value)}
                    disabled={loading}
                  />
                  <p className="text-xs text-muted-foreground">
                    Corrects for power losses between the grid connection point and the EV battery.
                    Must be between 0.5 and 1. Leave empty to apply no correction.
                  </p>
                </div>
              )}
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="edit-dryRun"
                  checked={dryRun}
                  onCheckedChange={(checked: boolean | 'indeterminate') => setDryRun(checked === true)}
                  disabled={loading}
                  className="mt-1"
                />
                <div className="flex-1">
                  <Label htmlFor="edit-dryRun" className="cursor-pointer flex items-center gap-2">
                    <TestTube className="h-4 w-4" />
                    Dry Run Mode
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Commands are logged but not executed (for testing)
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Forecast Configuration */}
          <div className="space-y-3 p-4 border rounded-lg bg-muted/50">
            <h3 className="font-medium text-sm">Forecast Configuration</h3>
            <p className="text-xs text-muted-foreground">
              Check to add a forecast purpose. Use the toggle to activate or deactivate it without removing it.
            </p>
            <div className="space-y-2">
              {FORECAST_PURPOSES.map(({ value, label, description }) => {
                const entry = forecastEntries.find((e) => e.purpose === value)
                const isIncluded = !!entry
                return (
                  <div key={value} className="flex items-start gap-3">
                    <Checkbox
                      id={`forecast-${value}`}
                      checked={isIncluded}
                      onCheckedChange={() => toggleForecastPurpose(value)}
                      disabled={loading}
                      className="mt-1 shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <Label htmlFor={`forecast-${value}`} className="text-sm cursor-pointer font-normal">
                        {label}
                      </Label>
                      <p className="text-xs text-muted-foreground">{description}</p>
                    </div>
                    {isIncluded && (
                      <Switch
                        checked={entry.isActive}
                        onCheckedChange={() => toggleForecastActive(value)}
                        disabled={loading}
                        aria-label={`${label} active`}
                        className="shrink-0 mt-0.5"
                      />
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Tags Section */}
          <Collapsible open={showTags} onOpenChange={setShowTags}>
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="outline"
                disabled={loading}
                className="w-full justify-between"
              >
                <span>Tags{tags.length > 0 ? ` (${tags.length})` : ' (Optional)'}</span>
                {showTags ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className="space-y-2 p-4 border rounded-lg mt-2">
                {tags.map((tag, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      placeholder="Key"
                      value={tag.key}
                      onChange={(e) => updateTag(index, 'key', e.target.value)}
                      disabled={loading}
                      className="flex-1"
                    />
                    <Input
                      placeholder="Value"
                      value={tag.value}
                      onChange={(e) => updateTag(index, 'value', e.target.value)}
                      disabled={loading}
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={() => removeTag(index)}
                      disabled={loading}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={addTag}
                  disabled={loading}
                  className="w-full"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Tag
                </Button>
              </div>
            </CollapsibleContent>
          </Collapsible>

          {/* Action Buttons */}
          <div className="flex gap-2 justify-end pt-4">
            <Button variant="outline" onClick={handleClose} disabled={loading}>
              Cancel
            </Button>
            <AuthorizedButton authorized={can('PoolsWrite')} onClick={handleSubmit} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Changes
            </AuthorizedButton>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
